import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        // RP ADS (Klone): al escoger medida o botella, deslizar hasta el frasco elegido (slot 3)
        if (newMediaGallery instanceof HTMLElement && newMediaGallery.dataset.kloneStory === 'true') {
          showKloneBottle(newMediaGallery);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * Slides a freshly rendered Klone gallery to the selected bottle once its slideshow is ready.
 * @param {HTMLElement} gallery
 */
function showKloneBottle(gallery, attempt = 0) {
  const slideshow = gallery.querySelector('slideshow-component');
  if (!gallery.isConnected || !slideshow || attempt > 20) return;

  const slides = /** @type {any} */ (slideshow).slides;
  if (!slides?.length) {
    setTimeout(() => showKloneBottle(gallery, attempt + 1), 50);
    return;
  }

  const index = slides.findIndex((/** @type {Element} */ slide) =>
    slide.querySelector('img')?.getAttribute('src')?.includes('klone-frasco-')
  );
  if (index <= 0) return;

  // One slide at a time: Horizon's long-jump reorder leaves the pagination out of sync.
  const wait = (/** @type {number} */ ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const slideToBottle = async () => {
    await wait(120);
    for (let step = 1; step <= index; step++) {
      if (!gallery.isConnected) return;
      await /** @type {any} */ (slideshow).select(step);
      await wait(380);
    }
  };

  // On mobile the picker sits below the gallery: wait until the shopper scrolls back up to see it.
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      slideToBottle();
    },
    { threshold: 0.6 }
  );
  observer.observe(slideshow);
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
